import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import { collection, onSnapshot, query, orderBy, doc, setDoc } from 'firebase/firestore';
import { firebaseService } from './FirebaseService';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Constants
const EXPO_PROJECT_ID = "d4595859-ba94-4a51-a3bb-f05ae30b5ba2";
const LAST_ANNOUNCEMENT_KEY = '@lastAnnouncementTimestamp';

// Get Firebase instances
const db = firebaseService.getDb();

// Configure notification handler
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

// Register for push notifications
export async function registerForPushNotificationsAsync() {
  try {
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('announcements', {
        name: 'Announcements',
        description: 'Receive notifications for new announcements',
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#FF231F7C',
        sound: 'default',
      });
    }

    if (!Device.isDevice) {
      console.error('Must use physical device for push notifications');
      return;
    }

    const { status: existingStatus } = await Notifications.getPermissionsAsync();
    let finalStatus = existingStatus;
    
    if (existingStatus !== 'granted') {
      console.log('Requesting notification permissions...');
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }
    
    if (finalStatus !== 'granted') {
      console.error('Failed to get push notification permissions');
      return;
    }

    console.log('Getting Expo push token...');
    const token = await Notifications.getExpoPushTokenAsync({
      projectId: EXPO_PROJECT_ID,
    });
    console.log('Expo Push Token:', token.data);
    
    // Store the token immediately after getting it
    await storePushToken(token.data);
    
    return token.data;
  } catch (error) {
    console.error('Error in registerForPushNotificationsAsync:', error);
  }
}

// Store the push token in AsyncStorage and Firestore
export async function storePushToken(token: string) {
  try {
    await AsyncStorage.setItem('@pushToken', token);
    
    // Store token in Firestore if user is logged in
    const currentUser = firebaseService.getCurrentUser();
    if (currentUser) {
      console.log('Storing token for user:', currentUser.uid);
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, {
        expoPushToken: token,
        updatedAt: new Date(),
      }, { merge: true });
      
      console.log('Push token stored successfully:', token);
    } else {
      console.warn('User not logged in, waiting for auth state change...');
      // Wait for auth state to change
      firebaseService.onAuthStateChanged(async (user) => {
        if (user) {
          console.log('User logged in, storing token for:', user.uid);
          const userRef = doc(db, 'users', user.uid);
          await setDoc(userRef, {
            expoPushToken: token,
            updatedAt: new Date(),
          }, { merge: true });
          console.log('Push token stored after auth:', token);
        }
      });
    }
  } catch (error) {
    console.error('Error storing push token:', error);
  }
}

// Store the user's account creation timestamp
export async function storeAccountCreationTimestamp() {
  try {
    const currentUser = firebaseService.getCurrentUser();
    if (currentUser) {
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, {
        createdAt: new Date(),
        updatedAt: new Date(),
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error storing account creation timestamp:', error);
  }
}

// Clear all announcement notifications
async function clearAllAnnouncementNotifications() {
  try {
    const notifications = await Notifications.getAllScheduledNotificationsAsync();
    for (const notification of notifications) {
      if (notification.content.data?.type === 'announcement') {
        await Notifications.cancelScheduledNotificationAsync(notification.identifier);
      }
    }
    
    // Also clear any displayed notifications
    const displayedNotifications = await Notifications.getPresentedNotificationsAsync();
    for (const notification of displayedNotifications) {
      if (notification.request.content.data?.type === 'announcement') {
        await Notifications.dismissNotificationAsync(notification.request.identifier);
      }
    }
  } catch (error) {
    console.error('Error clearing announcement notifications:', error);
  }
}

// Listen for new announcements
export function startAnnouncementListener() {
  console.log('Starting announcement listener...');
  try {
    const announcementsRef = collection(db, 'announcements');
    console.log('Announcements collection reference created');
    
    const q = query(announcementsRef, orderBy('createdAt', 'desc'));
    console.log('Query created with createdAt ordering');

    return onSnapshot(q, async (snapshot) => {
      console.log('Received announcement snapshot with', snapshot.docChanges().length, 'changes');
      const lastTimestamp = await getLastAnnouncementTimestamp();
      console.log('Last announcement timestamp:', new Date(lastTimestamp).toISOString());
      
      // Get only the most recent announcement change
      const changes = snapshot.docChanges();
      if (changes.length > 0) {
        const latestChange = changes[0];
        if (latestChange.type === 'added') {
          const announcement = latestChange.doc.data();
          console.log('New announcement detected:', {
            id: latestChange.doc.id,
            content: announcement.content,
            createdAt: announcement.createdAt?.toDate()
          });
          
          const announcementTimestamp = announcement.createdAt?.toMillis() || Date.now();
          console.log('Announcement timestamp:', new Date(announcementTimestamp).toISOString());

          // Only show notification for announcements created after the user's account
          if (announcementTimestamp > lastTimestamp) {
            console.log('New announcement is newer than account creation, showing notification');
            
            // Clear all existing announcement notifications first
            await clearAllAnnouncementNotifications();
            
            // Remove HTML tags from content for notification
            const cleanContent = announcement.content.replace(/<[^>]*>/g, '');
            
            try {
              await Notifications.scheduleNotificationAsync({
                content: {
                  title: 'New Announcement',
                  body: cleanContent,
                  data: { 
                    type: 'announcement',
                    announcementId: latestChange.doc.id,
                    imgurl1: announcement.imgurl1 || null
                  },
                  sound: 'default',
                },
                trigger: null, // Send immediately
              });
              console.log('Notification scheduled successfully');
              await updateLastAnnouncementTimestamp(announcementTimestamp);
              console.log('Last announcement timestamp updated to:', new Date(announcementTimestamp).toISOString());
            } catch (error) {
              console.error('Error scheduling notification:', error);
            }
          } else {
            console.log('Announcement is older than account creation, skipping notification');
          }
        }
      }
    }, (error) => {
      console.error('Error in announcement listener:', error);
    });
  } catch (error) {
    console.error('Error setting up announcement listener:', error);
    return () => {}; // Return empty cleanup function in case of error
  }
}

// Get the last announcement timestamp
async function getLastAnnouncementTimestamp(): Promise<number> {
  try {
    const timestamp = await AsyncStorage.getItem(LAST_ANNOUNCEMENT_KEY);
    return timestamp ? parseInt(timestamp) : 0;
  } catch (error) {
    console.error('Error getting last announcement timestamp:', error);
    return 0;
  }
}

// Update the last announcement timestamp
async function updateLastAnnouncementTimestamp(timestamp: number) {
  try {
    await AsyncStorage.setItem(LAST_ANNOUNCEMENT_KEY, timestamp.toString());
  } catch (error) {
    console.error('Error updating last announcement timestamp:', error);
  }
}

// Setup notification listeners
export function setupAnnouncementNotificationListeners() {
  console.log('Setting up announcement notification listeners...');
  
  const notificationListener = Notifications.addNotificationReceivedListener(notification => {
    console.log('Notification received:', notification);
  });

  const responseListener = Notifications.addNotificationResponseReceivedListener(response => {
    const announcementId = response.notification.request.content.data.announcementId;
    console.log('Notification tapped:', announcementId);
  });

  return () => {
    Notifications.removeNotificationSubscription(notificationListener);
    Notifications.removeNotificationSubscription(responseListener);
  };
}
